'use client'

import { useRouter } from 'next/navigation'
import { useCallback, useEffect } from 'react'

import { useConfirmDialog } from '../useConfirmDialog/useConfirmDialog'

type UnsavedChangesGuardOptions = {
    title?: string
    content?: string
}

/** Same-origin, same-tab, plain left-click on a link that leaves the current page — the clicks the guard intercepts. */
export const getGuardedNavigationHref = (event: MouseEvent, currentHref: string): string | null => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null
    const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
    if (!anchor || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return null
    const current = new URL(currentHref)
    const next = new URL(anchor.href, current)
    if (next.origin !== current.origin) return null
    if (next.pathname === current.pathname && next.search === current.search) return null
    return `${next.pathname}${next.search}${next.hash}`
}

/**
 * Asks before leaving a page with unsaved changes, using the shared confirm dialog:
 * - in-app links (sidebar, breadcrumbs, any same-origin `<a>`) are intercepted on click;
 * - `guard(fn)` wraps programmatic navigation such as a back button;
 * - tab close / reload falls back to `beforeunload` — browsers only allow their native prompt there.
 */
export const useUnsavedChangesGuard = (isDirty: boolean, options: UnsavedChangesGuardOptions = {}) => {
    const router = useRouter()
    const { confirm } = useConfirmDialog()
    const title = options.title ?? 'Discard changes?'
    const content = options.content ?? 'You have unsaved changes that will be lost if you leave this page.'

    const askToDiscard = useCallback(
        (onDiscard: () => void) => confirm({ title, content, color: 'error', confirmButtonText: 'Discard', onConfirm: onDiscard }),
        [confirm, title, content]
    )

    const guard = useCallback(
        (navigate: () => void) => {
            if (!isDirty) {
                navigate()
                return
            }
            askToDiscard(navigate)
        },
        [isDirty, askToDiscard]
    )

    useEffect(() => {
        if (!isDirty) return
        // Capture phase on document runs before React's root listener, so Next's <Link> never starts the navigation.
        const handleClick = (event: MouseEvent) => {
            const href = getGuardedNavigationHref(event, window.location.href)
            if (!href) return
            event.preventDefault()
            event.stopPropagation()
            askToDiscard(() => router.push(href))
        }
        const handleBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault()
        document.addEventListener('click', handleClick, true)
        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => {
            document.removeEventListener('click', handleClick, true)
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [isDirty, askToDiscard, router])

    return { guard }
}
